/**
 * Best-time-to-post recommendation schemas.
 *
 * Owner: prompts/02-features/post-scheduling/04-best-time-ai-opus-4.7.md
 *
 * The output schema is consumed by:
 *   - The DeepSeek-V3.2 generateObject() call in apps/api/src/services/best-time.ts
 *   - The tRPC `schedule.getBestTimes` procedure response type
 *   - The `useBestTimes` React Query hook
 *   - The `BestTimeSuggestion` calendar overlay component
 *
 * The 7-platform / 5-slot / 3-confidence-tier shape is fixed by contract:
 * UI assumes exactly 5 slots, downstream tests assume the enum closure.
 */

import { z } from "zod";

import { PLATFORMS } from "../constants/index.js";

/** ISO weekday names — matches the calendar's day-of-week selectors. */
export const BEST_TIME_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;
export type BestTimeDay = (typeof BEST_TIME_DAYS)[number];

/** ISO weekday number → name (1=Monday … 7=Sunday). Mirrors ClickHouse `toDayOfWeek`. */
export const ISO_WEEKDAY_TO_DAY: Record<number, BestTimeDay> = {
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
  7: "sunday",
};

/** Platforms supported by best-time analysis (matches PLATFORMS constant). */
export const bestTimePlatformSchema = z.enum(PLATFORMS);
export type BestTimePlatform = z.infer<typeof bestTimePlatformSchema>;

export const bestTimeSlotSchema = z.object({
  dayOfWeek: z.enum(BEST_TIME_DAYS),
  hour: z.number().int().min(0).max(23),
  minute: z.number().int().min(0).max(59),
  /** Confidence score 0–100. */
  score: z.number().min(0).max(100),
  /** Brief explanation of why this time is optimal. Capped to keep the AI prompt + UI tooltip tight. */
  reasoning: z.string().min(1).max(200),
});
export type BestTimeSlot = z.infer<typeof bestTimeSlotSchema>;

export const bestTimeConfidenceLevelSchema = z.enum(["high", "medium", "low"]);
export type BestTimeConfidenceLevel = z.infer<
  typeof bestTimeConfidenceLevelSchema
>;

export const bestTimeOutputSchema = z.object({
  platform: bestTimePlatformSchema,
  /** IANA timezone of the analysis (e.g. "Asia/Riyadh"). Never a UTC offset string. */
  timezone: z.string().min(1),
  /** Top 5 optimal posting times, sorted by score descending. */
  slots: z.array(bestTimeSlotSchema).length(5),
  /** Number of historical posts analyzed (zero for fallback). */
  dataPoints: z.number().int().min(0),
  confidenceLevel: bestTimeConfidenceLevelSchema,
  /**
   * Either an LLM model id (`"deepseek-chat-v3.2"`) or the literal
   * `"industry-defaults"` when the workspace has insufficient data.
   */
  analysisModel: z.string().min(1),
});
export type BestTimeOutput = z.infer<typeof bestTimeOutputSchema>;

export const getBestTimesInputSchema = z.object({
  workspaceId: z.string().min(1),
  platform: bestTimePlatformSchema,
  /** IANA timezone (e.g. "Asia/Riyadh"). MUST NOT be a UTC offset string. */
  timezone: z.string().min(1).default("UTC"),
  /** Bypass cache and re-run the analysis. */
  forceRefresh: z.boolean().default(false),
});
export type GetBestTimesInput = z.infer<typeof getBestTimesInputSchema>;
